"use client";

import { useState, useCallback, useEffect } from "react";

// ============================================================================
// TYPES
// ============================================================================

type TimeUnit =
  | "second"
  | "seconds"
  | "sec"
  | "secs"
  | "s"
  | "minute"
  | "minutes"
  | "min"
  | "mins"
  | "m"
  | "hour"
  | "hours"
  | "hr"
  | "hrs"
  | "h"
  | "day"
  | "days"
  | "d"
  | "week"
  | "weeks"
  | "w"
  | "month"
  | "months"
  | "mo"
  | "mos"
  | "year"
  | "years"
  | "yr"
  | "yrs"
  | "y";

type Duration = `${number} ${TimeUnit}` | `${number}${TimeUnit}`;

interface CookieOptions {
  /** Cookie path (default: "/") */
  path?: string;
  /** Domain the cookie belongs to */
  domain?: string;
  /**
   * Max age — accepts seconds (number) or a human-readable duration string.
   *
   * @example
   * maxAge: 31536000          // raw seconds
   * maxAge: "1 year"
   * maxAge: "2 weeks"
   * maxAge: "30 days"
   * maxAge: "12 hours"
   * maxAge: "90m"             // 90 minutes (no space is fine)
   */
  maxAgeSeconds?: number | Duration;
  /** Expiration date */
  expires?: Date;
  /** Send only over HTTPS */
  secure?: boolean;
  /** SameSite policy */
  sameSite?: "Strict" | "Lax" | "None";
  /** Prevent JavaScript access (set by server only, included for completeness) */
  httpOnly?: boolean;
}

interface ParsedCookie {
  name: string;
  value: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const UNIT_TO_SECONDS: Record<string, number> = {
  second: 1,
  seconds: 1,
  sec: 1,
  secs: 1,
  s: 1,
  minute: 60,
  minutes: 60,
  min: 60,
  mins: 60,
  m: 60,
  hour: 3600,
  hours: 3600,
  hr: 3600,
  hrs: 3600,
  h: 3600,
  day: 86400,
  days: 86400,
  d: 86400,
  week: 604800,
  weeks: 604800,
  w: 604800,
  month: 2592000,
  months: 2592000,
  mo: 2592000,
  mos: 2592000, // 30 days
  year: 31536000,
  years: 31536000,
  yr: 31536000,
  yrs: 31536000,
  y: 31536000, // 365 days
};

export const SITE_COOKIES = {
  locale: "NEXT_LOCALE",
} as const;

// ============================================================================
// COOKIE UTILITY FUNCTIONS (framework-agnostic, client-side)
// ============================================================================

/**
 * Convert a human-readable duration string to seconds.
 *
 * @example
 * durationToSeconds("1 year")    // 31536000
 * durationToSeconds("2 weeks")   // 1209600
 * durationToSeconds("30 days")   // 2592000
 * durationToSeconds("12h")       // 43200
 * durationToSeconds("90m")       // 5400
 * durationToSeconds(3600)        // 3600 (passthrough)
 */
export const durationToSeconds = (duration: number | Duration): number => {
  if (typeof duration === "number") return duration;

  const match = duration.trim().match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
  if (!match) {
    throw new Error(
      `Invalid duration format: "${duration}". Expected something like "1 year", "2 weeks", "30d", "90m".`,
    );
  }

  const amount = parseFloat(match[1]);
  const unit = match[2].toLowerCase();
  const multiplier = UNIT_TO_SECONDS[unit];

  if (multiplier === undefined) {
    throw new Error(
      `Unknown time unit: "${match[2]}". Supported: seconds, minutes, hours, days, weeks, months, years (and abbreviations).`,
    );
  }

  return Math.round(amount * multiplier);
};

// ============================================================================
// COOKIE UTILITY FUNCTIONS (framework-agnostic, client-side)
// ============================================================================

/**
 * Set a cookie with the given name, value, and options
 *
 * @example
 * setCookie("NEXT_LOCALE", "en", { path: "/", maxAgeSeconds: 31536000, sameSite: "Lax" })
 * setCookie("theme", "dark", { expires: new Date("2026-01-01"), secure: true });
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  if (typeof document === "undefined") return;

  const { path = "/", domain, maxAgeSeconds, expires, secure, sameSite = "Lax", httpOnly } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (path) cookieString += `; path=${path}`;
  if (domain) cookieString += `; domain=${domain}`;
  if (maxAgeSeconds !== undefined) cookieString += `; max-age=${durationToSeconds(maxAgeSeconds)}`;
  if (expires) cookieString += `; expires=${expires.toUTCString()}`;
  if (secure) cookieString += `; Secure`;
  if (sameSite) cookieString += `; SameSite=${sameSite}`;
  if (httpOnly) cookieString += `; HttpOnly`;

  document.cookie = cookieString;
};

/**
 * Get a single cookie value by name. Returns `null` if not found.
 *
 * @example
 * const locale = getCookie("NEXT_LOCALE"); // "en" | null
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split("; ");

  for (const cookie of cookies) {
    const [cookieName, ..._rest] = cookie.split("=");

    if (decodeURIComponent(cookieName) === name) {
      return decodeURIComponent(_rest.join("="));
    }
  }

  return null;
};

/**
 * Get all cookies as an array of { name, value } objects.
 *
 * @example
 * const all = getAllCookies(); // [{ name: "theme", value: "dark" }, ...]
 */
export const getAllCookies = (): ParsedCookie[] => {
  if (typeof document === "undefined") return [];
  if (!document.cookie) return [];

  return document.cookie.split("; ").map((cookie) => {
    const [name, ...rest] = cookie.split("=");

    return {
      name: decodeURIComponent(name),
      value: decodeURIComponent(rest.join("=")),
    };
  });
};

/**
 * Check if a cookie exists.
 *
 * @example
 * if (hasCookie("NEXT_LOCALE")) { ... }
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
};

/**
 * Delete a cookie by name. You must match the same `path` and `domain`
 * that were used when setting it.
 *
 * @example
 * deleteCookie("NEXT_LOCALE");
 * deleteCookie("session", { path: "/app", domain: ".example.com" });
 */
export const deleteCookie = (name: string, options: Pick<CookieOptions, "path" | "domain"> = {}): void => {
  setCookie(name, "", {
    ...options,
    path: options.path || "/",
    maxAgeSeconds: 0,
    expires: new Date(0),
  });
};

/**
 * Clear all visible cookies (those accessible via document.cookie).
 * Note: HttpOnly cookies cannot be cleared from the client.
 *
 * @example
 * clearAllClientCookies();
 */
export const clearAllClientCookies = (options: Pick<CookieOptions, "path" | "domain"> = {}): void => {
  getAllCookies().forEach(({ name }) => deleteCookie(name, options));
};

/** Set NEXT_LOCALE cookie (1 year, Lax, root path) */
export const setLocaleCookie = (locale: string): void => {
  setCookie(SITE_COOKIES.locale, locale, {
    path: "/",
    maxAgeSeconds: "1 year",
    sameSite: "Lax",
  });
};

/**
 * React hook to read, write, and delete a single cookie reactively.
 *
 * @example
 * const [locale, setLocale, removeLocale] = useCookie("NEXT_LOCALE", {
 *   defaultValue: "en",
 *   path: "/",
 *   maxAge: "1 year",
 *   sameSite: "Lax",
 * });
 *
 * setLocale("fr");   // updates cookie + state
 * removeLocale();    // deletes cookie + sets state to defaultValue
 */
export const useCookie = (
  name: string,
  options: CookieOptions & { defaultValue: string },
): [string, (value: string) => void, () => void] => {
  const { defaultValue, ...cookieOptions } = options;

  const [value, setValue] = useState<string>(() => {
    if (typeof document === "undefined") return defaultValue;

    return getCookie(name) || defaultValue;
  });

  useEffect(() => {
    const current = getCookie(name);

    if (current !== null) {
      setValue(current);
    }
  }, [name]);

  const updateCookie = useCallback(
    (newValue: string) => {
      setCookie(name, newValue, cookieOptions);
      setValue(newValue);
    },
    [name, cookieOptions],
  );

  const removeCookie = useCallback(() => {
    deleteCookie(name, { path: cookieOptions.path, domain: cookieOptions.domain });
    setValue(defaultValue);
  }, [name, defaultValue, cookieOptions.path, cookieOptions.domain]);

  return [value, updateCookie, removeCookie];
};

/**
 * React hook to observe all cookies. Re-renders when cookies change
 * (via polling every `intervalMs`).
 *
 * @example
 * const { cookies, set, remove, clear } = useCookies();
 */
export const useCookies = (intervalMs = 1000) => {
  const [cookies, setCookies] = useState<ParsedCookie[]>([]);

  useEffect(() => {
    const update = () => setCookies(getAllCookies());
    update();
    const id = setInterval(update, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const set = useCallback((name: string, value: string, opts?: CookieOptions) => {
    setCookie(name, value, opts);
    setCookies(getAllCookies());
  }, []);

  const remove = useCallback((name: string, opts?: Pick<CookieOptions, "path" | "domain">) => {
    deleteCookie(name, opts);
    setCookies(getAllCookies());
  }, []);

  const clear = useCallback((opts?: Pick<CookieOptions, "path" | "domain">) => {
    clearAllClientCookies(opts);
    setCookies([]);
  }, []);

  return { cookies, set, remove, clear };
};
